import {useEffect, useRef, useState, type RefObject} from 'react';
import {findScrollParent} from '@Utils/dom.util';

export function useStickyNav(navHeight: number): {
  isSticky: boolean;
  navRef: RefObject<HTMLDivElement | null>;
} {
  const [isSticky, setIsSticky] = useState(false);
  const navRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const navEl = navRef.current;
    if (!navEl) return;

    let scrollEl: HTMLElement | Window = findScrollParent(navEl) ?? window;

    const onScroll = (): void => {
      const scrollTop = scrollEl instanceof Window ? window.scrollY : scrollEl.scrollTop;
      setIsSticky(scrollTop >= navHeight);
    };

    const attach = (): void => {
      if (scrollEl instanceof Window) {
        window.addEventListener('scroll', onScroll, {passive: true});
      } else {
        scrollEl.addEventListener('scroll', onScroll, {passive: true});
      }
    };

    const detach = (): void => {
      if (scrollEl instanceof Window) {
        window.removeEventListener('scroll', onScroll);
      } else {
        scrollEl.removeEventListener('scroll', onScroll);
      }
    };

    attach();
    onScroll();

    const mo = new MutationObserver(() => {
      const newScrollEl: HTMLElement | Window = findScrollParent(navEl) ?? window;
      if (newScrollEl !== scrollEl) {
        detach();
        scrollEl = newScrollEl;
        attach();
        onScroll();
      }
    });

    mo.observe(document.body, {subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style']});

    return (): void => {
      detach();
      mo.disconnect();
    };
  }, [navHeight]);

  return {isSticky, navRef};
}
